import React, { useState, useEffect, useRef, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';

import getAdminProReservations from '../../../api/graphql/queries/getAdminProReservations';
import getAdminProReservationEvents from '../../../api/graphql/queries/getAdminProReservationEvents';

import ProReservationsTable from './ProReservationsTable';
import ProReservationsToolbar from './ProReservationsToolbar';
import LoadIcon from '../../LoadIcon';

const CET_TIMEZONE = 'Europe/Madrid';

const formatDateCET = (value) => {
  if (!value) return '';
  const m = moment(parseInt(value, 10) || value);
  return m.isValid() ? m.tz(CET_TIMEZONE).format('DD/MM/YYYY HH:mm') : '';
};

const ProReservationsContainer = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [downloadingInProgress, setDownloadingInProgress] = useState(false);

  const [email, setEmail] = useState(null);
  const [name, setName] = useState(null);
  const [eventSlugName, setEventSlugName] = useState(null);
  const [payed, setPayed] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [finishDate, setFinishDate] = useState(null);

  const [eventsList, setEventsList] = useState([]);

  const [sorting, setSorting] = useState(['createdAt', -1]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [previousPage, setPreviousPage] = useState(0);
  const [avoidEffect, setAvoidEffect] = useState(false);

  const isMounted = useRef(true);

  const columns = [
    { field: '_id', type: 'string', hide: true, isId: true, align: 'left' },
    {
      field: 'userName',
      headerName: 'Name',
      type: 'string',
      sortable: true,
      align: 'left',
      fieldFunc: row => [row.userName, row.userLastname].filter(Boolean).join(' '),
    },
    { field: 'userEmail', headerName: 'Email', type: 'string', sortable: true, align: 'left' },
    { field: 'eventName', headerName: 'Event', type: 'string', sortable: true, align: 'left' },
    { field: 'sessionDateTime', headerName: 'Session', type: 'dateCET', sortable: true, align: 'left' },
    { field: 'createdAt', headerName: 'Reserved at', type: 'dateCET', sortable: true, align: 'left' },
    {
      field: 'payed',
      headerName: 'Reserved',
      type: 'reserved',
      sortable: true,
      align: 'center',
    },
  ];

  const formatValue = ({ column, value, row }) => {
    if (column.fieldFunc && row) {
      const computed = column.fieldFunc(row);
      if (computed !== undefined) return computed;
    }
    if (value === null || value === undefined) return '';

    switch (column.type) {
      case 'dateCET':
        return formatDateCET(value);
      case 'reserved':
        return value ? 'Reserved' : 'Not reserved';
      case 'boolean':
        return value ? '✓' : '';
      default:
        return value;
    }
  };

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    getAdminProReservationEvents().then((events) => {
      if (isMounted.current) setEventsList(events || []);
    });
  }, []);

  useEffect(() => {
    if (avoidEffect) return;

    let from = page * rowsPerPage;
    let to = (page + 1) * rowsPerPage;
    if (previousPage === page) {
      setAvoidEffect(true);
      setPage(0);
      from = 0;
      to = rowsPerPage;
    } else {
      setPreviousPage(page);
    }

    getAdminProReservations({
      email,
      name,
      eventSlugName,
      payed,
      startDate,
      finishDate,
      from,
      to,
      sortingField: sorting[0],
      sortingType: sorting[1],
    }).then((result) => {
      if (!isMounted.current) return;

      if (result && result.isAuth) {
        setTotal(result.total);
        setFilteredData(result.reservations);
        setIsLoading(false);
      } else {
        console.log('getAdminProReservations failed!');
      }
      setAvoidEffect(false);
    });
  }, [email, name, eventSlugName, payed, startDate, finishDate, page, rowsPerPage, sorting]);

  const makeCsv = (rawData) => {
    const visibleForCsv = [
      ...columns.filter(c => !c.hide),
      { field: 'artistSetSlugName', headerName: 'Artist Set Slug', type: 'string' },
      { field: 'userId', headerName: 'User ID', type: 'string' },
    ];
    const numColumns = visibleForCsv.length;
    const csvArr = new Array(rawData.length + 1);
    const row = new Array(numColumns);

    csvArr[0] = visibleForCsv.map(c => c.headerName).join(',');

    for (let rowNum = 0; rowNum < rawData.length; rowNum++) {
      for (let colNum = 0; colNum < numColumns; colNum++) {
        const col = visibleForCsv[colNum];
        let val = formatValue({ column: col, value: rawData[rowNum][col.field], row: rawData[rowNum] });
        if (typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n'))) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        row[colNum] = val;
      }
      csvArr[rowNum + 1] = row.join(',');
    }
    return csvArr.join('\n');
  };

  const handleDownload = async () => {
    const chunkSize = 5000;
    setDownloadingInProgress(true);

    const { total: totalReservations } = await getAdminProReservations({
      email,
      name,
      eventSlugName,
      payed,
      startDate,
      finishDate,
      from: 0,
      to: 0,
    });

    const promises = [];
    for (let chunk = 0; chunk < Math.ceil(totalReservations / chunkSize); chunk++) {
      promises.push(getAdminProReservations({
        email,
        name,
        eventSlugName,
        payed,
        startDate,
        finishDate,
        from: chunk * chunkSize,
        to: chunkSize,
      }));
    }

    Promise.all(promises).then((rawDataChunks) => {
      const rawData = [];
      rawDataChunks.forEach(chunk => rawData.push(...chunk.reservations));

      const data = makeCsv(rawData);
      const element = document.createElement('a');
      const file = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      element.href = URL.createObjectURL(file);
      element.download = 'pro-reservations.csv';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      setDownloadingInProgress(false);
    });
  };

  return (
    <Fragment>
      <div className="w-full bg-white">
        <div className="flex justify-between mb-4">
          <span className="font-americaMonoBold text-14 uppercase">
            Total:
            {' '}
            {total}
          </span>
        </div>
        <div className="mt-4">
          {!isLoading ? (
            <>
              <ProReservationsToolbar
                email={email}
                setEmail={setEmail}
                name={name}
                setName={setName}
                eventSlugName={eventSlugName}
                setEventSlugName={setEventSlugName}
                payed={payed}
                setPayed={setPayed}
                startDate={startDate}
                setStartDate={setStartDate}
                finishDate={finishDate}
                setFinishDate={setFinishDate}
                eventsList={eventsList}
                handleDownload={handleDownload}
                downloadingInProgress={downloadingInProgress}
              />
              <ProReservationsTable
                tableData={filteredData}
                columns={columns}
                total={total}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                sorting={sorting}
                setSorting={setSorting}
                formatValue={formatValue}
                stickyHeader
              />
            </>
          ) : <LoadIcon />}
        </div>
      </div>
    </Fragment>
  );
};

export default withRouter(ProReservationsContainer);
